import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROLE_HOME } from '@sebastian/shared';
import { Button, Field, Input } from '../components/ui';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';

export function LoginPage() {
  const { signIn, status, home } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Where to land after signing in.
   *
   * `Protected` records the screen that bounced us here, and we return to it
   * rather than to the role's home. That matters for any deep link, but the
   * print station depends on it: it runs in a Chrome window with no address
   * bar, so a cashier dropped on /pos after the overnight token expiry has no
   * way back and the printer quietly stops serving the floor.
   */
  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
  const destination = from && from !== '/login' ? from : null;
  const [email, setEmail] = useState('admin@sebastian.local');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Sign in · Sebastian';
  }, []);

  if (status === 'authenticated') return <Navigate to={destination ?? home} replace />;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await signIn(email, password);
      navigate(destination ?? ROLE_HOME[user.role], { replace: true });
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-full items-center justify-center bg-canvas px-4 py-10">
      {/* A faint wash behind the card rather than a stock gradient. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0"
        style={{
          background:
            'radial-gradient(60rem 40rem at 50% -10%, rgba(47,111,178,0.08), transparent 60%)',
        }}
      />

      <div className="relative w-full max-w-md">
        <div className="mb-9 text-center">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full border border-accent/40 bg-accent/8">
            <span className="font-display text-4xl font-semibold text-accent-strong">S</span>
          </div>
          <h1 className="font-display text-4xl uppercase tracking-[0.32em] text-ink">Sebastian</h1>
          <div className="rule-accent mx-auto mt-4 w-32" />
          <p className="mt-4 text-sm tracking-wide text-ink-faint">Staff Point of Sale</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-line bg-surface p-7 shadow-xl"
        >
          <div className="space-y-4">
            <Field label="Email">
              <Input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@sebastian.local"
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
              />
            </Field>
          </div>

          {error ? (
            <p className="mt-4 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="primary" size="lg" block loading={busy} className="mt-6">
            Sign in
          </Button>
        </form>
      </div>
    </div>
  );
}
